import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Users, Lightbulb, Target, TrendingUp, Quote, Sparkles } from 'lucide-react';
import axios from 'axios';
import Reveal from '../components/Reveal';
import { getEventAccent } from '../lib/eventStyle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [events, setEvents] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [deftFeed, setDeftFeed] = useState([]);
  const [metrics, setMetrics] = useState({ members: '120+', age_group: '18-35', cities: '6' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, testimonialsRes, feedRes, metricsRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/deft-feed`),
          axios.get(`${API}/metrics`),
        ]);

        setEvents(eventsRes.data);
        setTestimonials(testimonialsRes.data);
        setDeftFeed(feedRes.data);
        setMetrics(metricsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="dark-container min-h-screen flex items-center justify-center" data-testid="home-loading">
        <p className="text-white">Loading...</p>
      </div>
    );
  }

  const valueProps = [
    {
      icon: Users,
      title: 'Curated Access',
      desc: 'Profile-based community ensuring quality connections and aligned values',
    },
    {
      icon: Target,
      title: 'Theme-Driven',
      desc: 'Events designed around specific interests, skills, and growth areas',
    },
    {
      icon: Lightbulb,
      title: 'Expert-Led',
      desc: 'Workshops and sessions facilitated by industry leaders and practitioners',
    },
    {
      icon: TrendingUp,
      title: 'Balanced Growth',
      desc: 'Professional development meets lifestyle experiences and learning',
    },
  ];

  return (
    <div className="dark-container" data-testid="home-page">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden">
        {/* Background grid + glow */}
        <div className="absolute inset-0 bg-grid-pattern opacity-70 pointer-events-none" />
        <div className="absolute -top-32 -left-32 w-[600px] h-[600px] bg-radial-teal opacity-60 pointer-events-none" />
        <div className="absolute top-1/3 -right-32 w-[500px] h-[500px] bg-radial-blue opacity-50 pointer-events-none" />

        <div className="dark-content-container relative pt-20 pb-32 lg:pt-28 lg:pb-48">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="flex items-center justify-center gap-3 animate-fade-in">
              <span className="w-2 h-2 rounded-full bg-[#00FFD1] pulse-dot" />
              <span className="eyebrow">Invite-only community</span>
            </div>

            <img
              src="/deft-logo.jpg"
              alt="Deft Experiences"
              className="w-80 md:w-[440px] mx-auto animate-fade-in-delay-1"
              data-testid="hero-logo"
            />

            <h1
              className="display-huge animate-fade-in-delay-2"
              style={{ color: '#64C8FF' }}
              data-testid="hero-headline"
            >
              Alchemy for the
              <br />
              <span className="text-glow-blue">Ambitious</span>
            </h1>

            <p className="body-large text-white/75 max-w-2xl mx-auto animate-fade-in-delay-3">
              Join a community of ambitious individuals focused on purposeful connections,
              skill-building, and authentic experiences
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-delay-4">
              <Link to="/events" className="btn-primary" data-testid="hero-view-events-btn">
                View Events
                <ArrowUpRight size={18} />
              </Link>
              <a
                href="https://deftexp.fillout.com/lessgo"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
                data-testid="hero-register-btn"
              >
                Register Your Interest
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============ METRICS ============ */}
      <section className="relative surface-1 border-y border-white/5">
        <div className="absolute inset-0 bg-grid-pattern-fine opacity-50 pointer-events-none" />
        <div className="dark-content-container relative py-20 lg:py-32">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {[
              { value: metrics.members, label: 'Community Members' },
              { value: metrics.age_group, label: 'Typical Age Group' },
              { value: metrics.cities, label: 'Cities Covered' },
            ].map((m, i) => (
              <Reveal
                key={i}
                delay={i * 120}
                className="text-center md:text-left md:border-l md:border-white/10 md:pl-8 first:md:border-l-0 first:md:pl-0"
                data-testid={`metric-${i}`}
              >
                <p className="metric-number text-[#00FFD1]">{m.value}</p>
                <p className="body-medium text-white/60 mt-3">{m.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ VALUE PROPS ============ */}
      <section className="relative">
        <div className="absolute inset-0 bg-grid-pattern opacity-40 pointer-events-none" />
        <div className="dark-content-container relative py-24 lg:py-36">
          <Reveal className="max-w-3xl mb-16">
            <span className="eyebrow mb-4 block">Why Deft</span>
            <h2 className="display-large text-white">Why Deft Experiences?</h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {valueProps.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal
                  key={i}
                  delay={i * 100}
                  className="group surface-2 border border-white/8 rounded-3xl p-8 dark-transition dark-hover relative overflow-hidden"
                  data-testid={`value-prop-${i}`}
                >
                  <div className="absolute -top-12 -right-12 w-40 h-40 bg-radial-teal opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                  <div className="relative">
                    <div className="w-14 h-14 rounded-2xl glass-teal flex items-center justify-center mb-6">
                      <Icon className="text-[#00FFD1]" size={26} />
                    </div>
                    <h3 className="heading-2 text-white mb-3">{v.title}</h3>
                    <p className="body-small text-white/65">{v.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ UPCOMING EVENTS ============ */}
      <section className="relative surface-1">
        <div className="dark-content-container py-24 lg:py-36">
          <Reveal className="flex flex-col md:flex-row md:justify-between md:items-end gap-6 mb-14">
            <div>
              <span className="eyebrow mb-4 block">Calendar</span>
              <h2 className="display-large text-white">Upcoming Events</h2>
            </div>
            <Link
              to="/events"
              className="inline-flex items-center gap-2 text-[#00FFD1] hover:text-white transition-colors body-large font-medium"
              data-testid="upcoming-view-all"
            >
              View All
              <ArrowUpRight size={20} />
            </Link>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.slice(0, 3).map((event, i) => {
              const accent = getEventAccent(event.type);
              return (
              <Reveal key={event.id} delay={i * 100}>
                <Link
                  to={`/events/${event.id}`}
                  className="group surface-2 border border-white/8 rounded-3xl overflow-hidden dark-transition dark-hover flex flex-col h-full"
                  data-testid={`home-event-card-${event.id}`}
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={event.image}
                      alt={event.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                    <span className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase ${accent.pill}`}>
                      {event.type}
                    </span>
                  </div>
                  <div className="p-7 flex-1 flex flex-col">
                    <p className={`body-small ${accent.text} mb-2 font-medium`}>{event.date}</p>
                    <h3 className={`heading-2 text-white mb-4 ${accent.hoverText} transition-colors`}>
                      {event.name}
                    </h3>
                    <div className="mt-auto pt-4 border-t border-white/8 flex items-center justify-between">
                      <p className={`body-large ${accent.text} font-semibold`}>{event.price}</p>
                      <ArrowUpRight className={`text-white/40 ${accent.hoverText} group-hover:translate-x-1 group-hover:-translate-y-1 transition-all`} size={20} />
                    </div>
                  </div>
                </Link>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ NERD FEED TEASER ============ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-40 pointer-events-none" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-radial-teal opacity-50 pointer-events-none" />
        <div className="dark-content-container relative py-24 lg:py-36 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-teal mb-6">
              <Sparkles size={14} className="text-[#00FFD1]" />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#00FFD1]">Coming Soon</span>
            </div>
            <h2 className="display-large text-white mb-4">The Nerd Feed</h2>
            <p className="body-large text-white/65 max-w-xl mx-auto mb-8">
              Anti-brainrot learning for curious minds
            </p>
            <p className="display-medium text-[#00FFD1] text-glow-teal">Coming Soon</p>
          </Reveal>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="relative surface-1">
        <div className="dark-content-container py-24 lg:py-36">
          <Reveal className="max-w-3xl mb-16">
            <span className="eyebrow mb-4 block">Voices</span>
            <h2 className="display-large text-white">What Our Community Says</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((t, i) => (
              <Reveal
                key={t.id}
                delay={i * 120}
                className="surface-2 border border-white/8 rounded-3xl p-10 relative overflow-hidden h-full"
                data-testid={`testimonial-${t.id}`}
              >
                <Quote
                  className="absolute top-6 right-6 text-[#00FFD1]/15"
                  size={48}
                  strokeWidth={1.5}
                />
                <div className="relative">
                  <p className="text-xl md:text-2xl text-white font-light leading-snug mb-8 font-display pr-12">
                    "{t.quote}"
                  </p>
                  <div className="flex items-center gap-4 pt-6 border-t border-white/8">
                    <img
                      src={t.avatar}
                      alt={t.author}
                      className="w-14 h-14 rounded-full object-cover ring-2 ring-[#00FFD1]/30"
                    />
                    <div>
                      <p className="heading-3 text-white">{t.author}</p>
                      <p className="body-small text-white/50">{t.role}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative">
        <div className="dark-content-container py-24 lg:py-36">
          <Reveal className="relative overflow-hidden surface-2 border border-white/10 rounded-[2.5rem] p-12 md:p-20 text-center">
            <div className="absolute inset-0 bg-grid-pattern opacity-50 pointer-events-none" />
            <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-radial-teal opacity-60 pointer-events-none" />
            <div className="relative">
              <span className="eyebrow mb-6 block">Join the journey</span>
              <h2 className="display-large text-white mb-6">
                Ready to Join the <span className="text-[#64C8FF]">Community</span>?
              </h2>
              <p className="body-large text-white/70 mb-10 max-w-2xl mx-auto">
                Submit your profile and become part of a curated network of ambitious,
                growth-oriented individuals
              </p>
              <a
                href="https://deftexp.fillout.com/lessgo"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex"
                data-testid="home-cta-register-btn"
              >
                Register Your Interest
                <ArrowUpRight size={18} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
